import {Component, Input, ViewEncapsulation} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {WeeklyEd15SurveysV2, WeeklySeverityV2} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {TrendDirection} from '@shared/api/models/query/patient-daily-snapshot.model';
import {TrendIconComponent} from '@shared/components/trend-icon/trend-icon.component';
import {WeeklyStateLabelComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-state-label/weekly-state-label.component';

export interface WellbeingQuizRow {
    num: number;
    label: string;
    /** null = Not applicable (UNIVAPLAT-3054) — never rendered or treated as 0. */
    value: number | null;
    trend: TrendDirection | null;
}

/**
 * ED-15 detail pop-up — v2 contract (UNIVAPLAT-2962 OQ-FE4, reversed 2026-09-24: kept
 * rather than removed). Reads `surveys.answers` (keys "1"-"15", added on the backend by
 * UNIVAPLAT-3054) and `surveys.overallScore` instead of the legacy per-code `answers` /
 * `score` fields. A `null` answer value is "Not applicable" (e.g. ED-15 Q11-Q15 behaviour
 * items), rendered via the shared `cdss-weekly-state-label`, never as 0.
 */
@Component({
    selector: 'cdss-wellbeing-quiz-modal',
    standalone: true,
    imports: [CommonModule, TrendIconComponent, WeeklyStateLabelComponent],
    templateUrl: './wellbeing-quiz-modal.component.html',
    styleUrl: './wellbeing-quiz-modal.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class WellbeingQuizModalComponent {

    static readonly QUESTIONS: Record<number, string> = {
        1:  'Worried about losing control over my eating?',
        2:  'Avoided activities or people because of the way I look?',
        3:  'Been preoccupied with thoughts of food and eating?',
        4:  'Compared my body negatively with others?',
        5:  'Avoided looking at my body (e.g., in mirrors; wearing baggy clothes) because of the way it makes me feel?',
        6:  'Felt distressed about my weight?',
        7:  'Checked my body to reassure myself about my appearance (e.g., weighing myself; using mirrors)?',
        8:  'Followed strict rules about my eating?',
        9:  'Felt distressed about my body shape?',
        10: 'Worried that other people were judging me as a person because of my weight and appearance?',
        11: 'Over the past week, how many times have you binged (felt out of control of your eating, and eaten far more than a person normally would at one go)?',
        12: 'Over the past week, how many times have you vomited to control your weight (whether you had to make yourself sick or not) (i.e., using your fingers or medicines to make yourself sick, or vomiting without such aids)?',
        13: 'How many DAYS in the past week have you used laxatives to control your weight or shape?',
        14: 'How many DAYS in the past week have you restricted or dieted in order to control your weight?',
        15: 'How many DAYS in the past week have you exercised hard in order to control our weight?',
    };

    static readonly SCALE_LABELS: Record<number, string> = {
        0: 'Not at all',
        1: 'Rarely',
        2: 'Occasionally',
        3: 'Sometimes',
        4: 'Often',
        5: 'Most of the time',
        6: 'All the time',
    };

    @Input() surveys!: WeeklyEd15SurveysV2;

    constructor(public modal: NgbActiveModal) {}

    isScaleQuestion(num: number): boolean {
        return num >= 1 && num <= 10;
    }

    getScaleLabel(value: number): string {
        return WellbeingQuizModalComponent.SCALE_LABELS[value] ?? String(value);
    }

    get rows(): WellbeingQuizRow[] {
        if (!this.surveys?.answers) return [];
        return Object.entries(this.surveys.answers)
            .map(([num, answer]) => ({
                num: Number(num),
                label: WellbeingQuizModalComponent.QUESTIONS[Number(num)] ?? `Item ${num}`,
                value: answer.value,
                trend: answer.trend,
            }))
            .sort((a, b) => a.num - b.num);
    }

    get totalScore(): number | null {
        return this.surveys?.overallScore ?? null;
    }

    get severity(): WeeklySeverityV2 | null {
        return this.surveys?.overallSeverity ?? null;
    }

    get trend(): TrendDirection | null {
        return this.surveys?.trend ?? null;
    }

    get displayName(): string {
        return 'Eating Disorder-15 (ED-15)';
    }
}
